import { Play } from '@tunasong/icons'
import { INTERVAL_POOL } from '@tunasong/music-lib'
import type { IntervalQuestion, IntervalVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './interval-feedback.module.css'

export interface IntervalFeedbackProps {
  question: IntervalQuestion
  verdict: IntervalVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Ear Training Interval ID post-answer reveal. Renders:
 *  - Verdict header ("Correct." / "Not quite.") in success/error colour.
 *  - Correct-answer sentence naming the interval + (on incorrect) the
 *    learner's pick. Format: "That was a Major 3rd. You picked Minor 3rd."
 *  - Level-resolved lexicon body for the interval (semitone count + landmark).
 *  - "Hear it again" + manual "Next" actions (no auto-advance — Bowie §Q5).
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.3
 */
export const IntervalFeedback: FC<IntervalFeedbackProps> = props => {
  const { question, verdict, onHearAgain, onNext } = props
  const correctEntry = verdict.entry
  const pickedEntry = INTERVAL_POOL.find(e => e.kind === verdict.picked) ?? correctEntry
  const lexicon = useTheoryCopy(correctEntry.lexiconId)
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  const directionTag =
    question.direction === 'harmonic'
      ? 'played together'
      : question.direction === 'descending'
        ? 'descending'
        : 'ascending'
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        {verdict.correct ? (
          <>
            That was a {correctEntry.displayName}, {directionTag}.
          </>
        ) : (
          <>
            That was a {correctEntry.displayName}, {directionTag}. You picked {pickedEntry.displayName}.
          </>
        )}
      </p>
      {lexBody ? (
        <p className={styles.body}>
          <TheoryGlyphs>{lexBody}</TheoryGlyphs>
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button variant="text" size="small" startIcon={<Play />} onClick={onHearAgain}>
          Hear it again
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
